import React, { useState } from 'react';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { Loading } from '../components/Loading';
import { showToast } from '../utils/toast';
import { financeService, FinanceData } from '../services/financeService';
import { exportService } from '../services/exportService';
import { format } from 'date-fns';
import { formatCurrency } from '../utils/currency';

export const Reports: React.FC = () => {
  const [reportType, setReportType] = useState<'daily' | 'monthly' | 'yearly' | 'range'>('monthly');
  const [reportData, setReportData] = useState<FinanceData | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState<'pdf' | 'excel' | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const categories = ['Tyre', 'Battery', 'Rims'];

  // Date inputs
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const generateReport = async () => {
    setLoading(true);

    try {
      const category = selectedCategory === 'all' ? undefined : selectedCategory;
      let response;

      switch (reportType) {
        case 'daily':
          response = await financeService.getDailyFinance(selectedDate, category);
          break;
        case 'monthly':
          response = await financeService.getMonthlyFinance(selectedMonth, category);
          break;
        case 'yearly':
          response = await financeService.getYearlyFinance(parseInt(selectedYear), category);
          break;
        case 'range':
          if (!startDate || !endDate) {
            showToast.error('Please select both start and end dates');
            setLoading(false);
            return;
          }
          response = await financeService.getFinanceRange(startDate, endDate, category);
          break;
      }

      if (response.success && response.data) {
        setReportData(response.data);
      } else {
        showToast.error(response.message || 'Failed to generate report');
      }
    } catch (err: any) {
      showToast.error(err.message || 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  // Import formatCurrency from utils instead

  const handleExportPDF = async () => {
    if (!reportData) {
      showToast.error('Please generate a report first');
      return;
    }

    try {
      setExporting('pdf');

      const params: any = { type: reportType };
      if (reportType === 'daily') {
        params.date = selectedDate;
      } else if (reportType === 'monthly') {
        params.month = selectedMonth;
      } else if (reportType === 'yearly') {
        params.year = parseInt(selectedYear);
      } else if (reportType === 'range') {
        params.start_date = startDate;
        params.end_date = endDate;
      }
      if (selectedCategory !== 'all') {
        params.category = selectedCategory;
      }

      const blob = await exportService.exportFinancePDF(params);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const categorySuffix = selectedCategory !== 'all' ? `_${selectedCategory}` : '';
      const periodSuffix = reportType === 'daily' ? `_${selectedDate}` :
        reportType === 'monthly' ? `_${selectedMonth}` :
          reportType === 'yearly' ? `_${selectedYear}` :
            reportType === 'range' ? `_${startDate}_to_${endDate}` : '';
      a.download = `financial_report${categorySuffix}${periodSuffix}_${format(new Date(), 'yyyyMMdd_HHmmss')}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      showToast.error(err.message || 'Failed to export PDF');
    } finally {
      setExporting(null);
    }
  };

  const handleExportExcel = async () => {
    if (!reportData) {
      showToast.error('Please generate a report first');
      return;
    }

    try {
      setExporting('excel');

      const params: any = { type: reportType };
      if (reportType === 'daily') {
        params.date = selectedDate;
      } else if (reportType === 'monthly') {
        params.month = selectedMonth;
      } else if (reportType === 'yearly') {
        params.year = parseInt(selectedYear);
      } else if (reportType === 'range') {
        params.start_date = startDate;
        params.end_date = endDate;
      }
      if (selectedCategory !== 'all') {
        params.category = selectedCategory;
      }

      const blob = await exportService.exportFinanceExcel(params);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const categorySuffix = selectedCategory !== 'all' ? `_${selectedCategory}` : '';
      const periodSuffix = reportType === 'daily' ? `_${selectedDate}` :
        reportType === 'monthly' ? `_${selectedMonth}` :
          reportType === 'yearly' ? `_${selectedYear}` :
            reportType === 'range' ? `_${startDate}_to_${endDate}` : '';
      a.download = `financial_report${categorySuffix}${periodSuffix}_${format(new Date(), 'yyyyMMdd_HHmmss')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      showToast.error(err.message || 'Failed to export Excel');
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Financial Reports</h1>

      <Card title="Generate Report" className="mb-6">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Report Type
            </label>
            <div className="flex space-x-2">
              <button
                className={`px-4 py-2 rounded-lg font-medium ${reportType === 'daily'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                onClick={() => setReportType('daily')}
              >
                Daily
              </button>
              <button
                className={`px-4 py-2 rounded-lg font-medium ${reportType === 'monthly'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                onClick={() => setReportType('monthly')}
              >
                Monthly
              </button>
              <button
                className={`px-4 py-2 rounded-lg font-medium ${reportType === 'yearly'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                onClick={() => setReportType('yearly')}
              >
                Yearly
              </button>
              <button
                className={`px-4 py-2 rounded-lg font-medium ${reportType === 'range'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                onClick={() => setReportType('range')}
              >
                Date Range
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {reportType === 'daily' && (
              <Input
                label="Select Date"
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
              />
            )}
            {reportType === 'monthly' && (
              <Input
                label="Select Month"
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
              />
            )}
            {reportType === 'yearly' && (
              <Input
                label="Select Year"
                type="number"
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                min="2020"
                max={new Date().getFullYear()}
              />
            )}
            {reportType === 'range' && (
              <>
                <Input
                  label="Start Date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
                <Input
                  label="End Date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Filter by Category
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Items</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <Button onClick={generateReport} disabled={loading}>
            {loading ? 'Generating...' : 'Generate Report'}
          </Button>
        </div>
      </Card>

      {loading && (
        <Card>
          <Loading text="Generating report..." />
        </Card>
      )}

      {reportData && !loading && (
        <Card title="Report Results">
          <div className="space-y-6">
            {/* Report Header */}
            <div className="border-b pb-4">
              <h2 className="text-xl font-semibold text-gray-900">
                {reportType === 'daily' && `Daily Report - ${format(new Date(selectedDate), 'MMMM dd, yyyy')}`}
                {reportType === 'monthly' && `Monthly Report - ${format(new Date(selectedMonth + '-01'), 'MMMM yyyy')}`}
                {reportType === 'yearly' && `Yearly Report - ${selectedYear}`}
                {reportType === 'range' && `Date Range Report - ${format(new Date(startDate), 'MMM dd, yyyy')} to ${format(new Date(endDate), 'MMM dd, yyyy')}`}
              </h2>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-lg border border-green-200">
                <p className="text-sm font-medium text-green-700 mb-1">Total Revenue</p>
                <p className="text-2xl font-bold text-green-700">
                  {formatCurrency(reportData.total_revenue)}
                </p>
              </div>
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-4 rounded-lg border border-gray-200">
                <p className="text-sm font-medium text-gray-700 mb-1">Total Cost</p>
                <p className="text-2xl font-bold text-gray-800">
                  {formatCurrency(reportData.total_cost)}
                </p>
              </div>
              <div className="bg-gradient-to-br from-green-50 to-emerald-100 p-4 rounded-lg border border-green-300">
                <p className="text-sm font-medium text-green-700 mb-1">Total Profit</p>
                <p className="text-2xl font-bold text-green-700">
                  {formatCurrency(reportData.total_profit || (reportData.profit_loss > 0 ? reportData.profit_loss : 0))}
                </p>
              </div>
              <div className="bg-gradient-to-br from-red-50 to-red-100 p-4 rounded-lg border border-red-300">
                <p className="text-sm font-medium text-red-700 mb-1">Total Loss</p>
                <p className="text-2xl font-bold text-red-700">
                  {formatCurrency(reportData.total_loss || (reportData.profit_loss < 0 ? Math.abs(reportData.profit_loss) : 0))}
                </p>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-lg border border-blue-200">
                <p className="text-sm font-medium text-blue-700 mb-1">Total Sales</p>
                <p className="text-2xl font-bold text-blue-700">
                  {reportData.total_sales || 0}
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  {reportData.total_quantity || 0} items sold
                </p>
              </div>
            </div>

            {/* Item Breakdown by Category */}
            {reportData.item_breakdown && reportData.item_breakdown.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold mb-4">Item Breakdown by Category</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Category
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Revenue
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Cost
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Profit
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Loss
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Quantity
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Sales
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {reportData.item_breakdown.map((category, index) => (
                        <React.Fragment key={index}>
                          <tr className="bg-blue-50 hover:bg-blue-100">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="text-sm font-bold text-blue-900">{category.category}</span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600">
                              {formatCurrency(category.total_revenue)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                              {formatCurrency(category.total_cost)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600">
                              {formatCurrency(category.total_profit)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-red-600">
                              {formatCurrency(category.total_loss)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                              {category.total_quantity}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                              {category.total_sales}
                            </td>
                          </tr>
                          {category.items.map((item, itemIndex) => (
                            <tr key={`${index}-${itemIndex}`} className="hover:bg-gray-50">
                              <td className="px-6 py-2 pl-12 text-sm text-gray-600">
                                <div className="flex flex-col">
                                  <span className="font-medium">{item.item_name}</span>
                                  <span className="text-xs text-gray-500">{item.item_brand}</span>
                                </div>
                              </td>
                              <td className="px-6 py-2 text-sm text-green-600">
                                {formatCurrency(item.total_revenue)}
                              </td>
                              <td className="px-6 py-2 text-sm text-gray-600">
                                {formatCurrency(item.total_cost)}
                              </td>
                              <td className="px-6 py-2 text-sm text-green-600">
                                {formatCurrency(item.total_profit)}
                              </td>
                              <td className="px-6 py-2 text-sm text-red-600">
                                {formatCurrency(item.total_loss)}
                              </td>
                              <td className="px-6 py-2 text-sm text-gray-600">
                                {item.total_quantity}
                              </td>
                              <td className="px-6 py-2 text-sm text-gray-600">
                                {item.total_sales}
                              </td>
                            </tr>
                          ))}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Detailed Breakdown */}
            {reportData.daily_breakdown && reportData.daily_breakdown.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold mb-4">Daily Breakdown</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Revenue
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Cost
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Profit
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Loss
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Sales
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {reportData.daily_breakdown.map((day, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {format(new Date(day.date!), 'MMM dd, yyyy')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-medium">
                            {formatCurrency(day.total_revenue)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {formatCurrency(day.total_cost)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                            {formatCurrency(day.total_profit || (day.profit_loss > 0 ? day.profit_loss : 0))}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-red-600">
                            {formatCurrency(day.total_loss || (day.profit_loss < 0 ? Math.abs(day.profit_loss) : 0))}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {day.total_sales || 0}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Export Options */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  Export this report for your records
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    onClick={handleExportPDF}
                    disabled={exporting === 'pdf'}
                  >
                    {exporting === 'pdf' ? 'Exporting...' : '📄 Export PDF'}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={handleExportExcel}
                    disabled={exporting === 'excel'}
                  >
                    {exporting === 'excel' ? 'Exporting...' : '📊 Export Excel'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};
